import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, FileText, CheckCircle2, XCircle } from "lucide-react";
import { api, errMsg, streamAI } from "@/lib/api";
import { SimpleSelect, MultiCheck } from "@/components/FormField";
import { Markdown } from "@/components/ModulePage";
import { StatusBadge } from "@/components/Brand";
import { DOC_TYPES } from "@/constants/options";

export const splitDocs = (s) => (s || "").split(/;|\n/).map((x) => x.trim()).filter(Boolean);

export default function GenerateDocDialog({ open, onClose, clause, preset }) {
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [picked, setPicked] = useState([]);
  const [type, setType] = useState("");
  const [answers, setAnswers] = useState({});
  const [instr, setInstr] = useState("");
  const [mode, setMode] = useState("new");
  const [reviseId, setReviseId] = useState("");
  const [run, setRun] = useState(null);
  const titles = splitDocs(clause?.documents_required);
  useEffect(() => {
    if (!open || !clause) return;
    setRun(null); setAnswers({}); setInstr(""); setMode("new"); setReviseId("");
    setPicked(preset ? [preset] : titles.slice(0, 1));
    setType(DOC_TYPES.includes(clause.doc_type) ? clause.doc_type : "SOP");
    api.post("/ai/missing-info", { clause_id: clause.id, doc_type: clause.doc_type }).then((r) => setInfo(r.data)).catch(() => setInfo({ fields: [], existing: [] }));
    // eslint-disable-next-line
  }, [open, clause?.id]);
  const go = async () => {
    const prof = Object.fromEntries((info?.fields || []).filter((f) => f.profile && answers[f.key]?.length).map((f) => [f.key, answers[f.key]]));
    if (Object.keys(prof).length) await api.put("/lab/profile", { profile: prof }).catch(() => {});
    const list = mode === "revise" ? [info.existing.find((e) => e.id === reviseId)?.title] : picked;
    const res = list.map((t) => ({ title: t, state: "pending", text: "" }));
    setRun([...res]);
    for (let i = 0; i < list.length; i++) {
      res[i].state = "running"; setRun([...res]);
      try {
        const out = await streamAI({ task: "generate_document", clause_id: clause.id, doc_title: list[i], doc_type: type, answers, instructions: instr, mode, document_id: mode === "revise" ? reviseId : undefined },
          (t) => { res[i].text = t; setRun([...res]); });
        res[i].state = "done"; res[i].id = out.document_id;
      } catch (e) { res[i].state = "failed"; res[i].error = e.message; }
      setRun([...res]);
    }
  };
  const busy = run?.some((r) => r.state === "running" || r.state === "pending");
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="generate-doc-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#48217A]" />Generate document · Clause {clause?.clause_no}</DialogTitle>
          <DialogDescription>AI drafts use the clause, NABL 112A criteria and your lab profile. Drafts are never auto-approved.</DialogDescription>
        </DialogHeader>
        {!run ? (
          <div className="space-y-4">
            {info?.existing?.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="existing-docs-warning">
                <div className="text-sm font-medium text-amber-800">Existing documents are linked to this clause</div>
                <div className="space-y-1.5 mt-2">
                  {info.existing.map((e) => (
                    <div key={e.id} className="flex items-center gap-2 text-[13px]"><FileText className="h-3.5 w-3.5" />{e.code || "—"} {e.title} v{e.version}<StatusBadge status={e.status} />
                      <Button size="sm" variant="ghost" className="ml-auto h-7" onClick={() => nav(`/documents/${e.id}`)} data-testid={`open-existing-${e.id}`}>Open</Button></div>
                  ))}
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant={mode === "new" ? "default" : "outline"} onClick={() => setMode("new")} data-testid="mode-new">Create new draft</Button>
                  <Button size="sm" variant={mode === "revise" ? "default" : "outline"} onClick={() => { setMode("revise"); setReviseId(info.existing[0].id); }} data-testid="mode-revise">Revise existing (new version)</Button>
                </div>
                {mode === "revise" && <div className="mt-2"><SimpleSelect value={reviseId} onChange={setReviseId} options={info.existing.map((e) => [e.id, `${e.title} v${e.version}`])} testid="revise-select" /></div>}
              </div>
            )}
            {mode === "new" && (
              <div className="space-y-1.5"><Label>Documents to generate (select one or more for batch)</Label>
                <MultiCheck value={picked} onChange={setPicked} options={[...new Set([...titles, ...(preset ? [preset] : [])])].length ? [...new Set([...titles, ...(preset ? [preset] : [])])] : [`${clause?.title} ${clause?.doc_type}`]} testid="generate-doc-titles" cols={1} />
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Document type</Label><SimpleSelect value={type} onChange={setType} options={DOC_TYPES} testid="generate-doc-type" /></div>
              {(info?.fields || []).map((f) => (
                <div key={f.key} className={`space-y-1.5 ${f.type === "multi" ? "sm:col-span-2" : ""}`}>
                  <Label>{f.label} {f.profile && <span className="text-[10px] text-amber-600">(missing from profile)</span>}</Label>
                  {f.type === "multi" ? <MultiCheck value={answers[f.key] || []} onChange={(v) => setAnswers({ ...answers, [f.key]: v })} options={f.options} cols={3} />
                    : f.type === "select" ? <SimpleSelect value={answers[f.key]} onChange={(v) => setAnswers({ ...answers, [f.key]: v })} options={f.options} testid={`gen-field-${f.key}`} />
                    : <input className="h-9 w-full rounded-md border px-3 text-sm" value={answers[f.key] || ""} onChange={(e) => setAnswers({ ...answers, [f.key]: e.target.value })} placeholder="Leave blank to use [placeholder]" data-testid={`gen-field-${f.key}`} />}
                </div>
              ))}
            </div>
            <div className="space-y-1.5"><Label>Extra instructions (optional)</Label><Textarea rows={2} value={instr} onChange={(e) => setInstr(e.target.value)} placeholder="e.g. Include Westgard multi-rules for biochemistry" data-testid="generate-doc-instructions" /></div>
            <div className="flex justify-end"><Button onClick={go} disabled={mode === "new" ? !picked.length : !reviseId} className="bg-[#48217A] hover:bg-[#3B1A66]" data-testid="generate-doc-start"><Sparkles className="h-4 w-4 mr-1" />Generate {mode === "new" && picked.length > 1 ? `${picked.length} documents` : "draft"}</Button></div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="generate-doc-progress">
            {run.map((r, i) => (
              <div key={i} className="rounded-lg border p-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  {r.state === "done" ? <CheckCircle2 className="h-4 w-4 text-[#2E9B65]" /> : r.state === "failed" ? <XCircle className="h-4 w-4 text-[#D64545]" /> : <Loader2 className="h-4 w-4 animate-spin text-[#48217A]" />}
                  {r.title}<span className="ml-auto text-xs text-[#5A6B87]">{r.state}</span>
                  {r.id && <Button size="sm" className="h-7 bg-[#2E9B65]" onClick={() => nav(`/documents/${r.id}`)} data-testid={`open-generated-${i}`}>Open draft</Button>}
                </div>
                {r.error && <p className="text-xs text-[#D64545] mt-1">{r.error}</p>}
                {r.state === "running" && <div className="mt-2 max-h-56 overflow-y-auto bg-slate-50 rounded p-2"><Markdown>{r.text}</Markdown></div>}
              </div>
            ))}
            {!busy && <div className="flex justify-end"><Button variant="outline" onClick={onClose} data-testid="generate-doc-close">Close</Button></div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export const notifyErr = (e) => toast.error(errMsg(e));
